import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import type { OrderSummary } from '../../../../infrastructure/OMSClient';

// Parxl label size: 100mm x 150mm (1mm = 2.83465 points)
const PAGE_WIDTH = 283; // 100mm
const PAGE_HEIGHT = 425; // 150mm
const MARGIN = 15;
const ROW_HEIGHT = 28;
const HEADER_HEIGHT = 24;
const ITEMS_PER_PAGE = 8;

interface LineItemForPrint {
  name: string;
  nameKor?: string | undefined;
  quantity: number;
}

async function loadKoreanBoldFont(): Promise<ArrayBuffer> {
  const response = await fetch('/fonts/NotoSansKR-Bold.otf');
  return response.arrayBuffer();
}

async function drawPackingVerificationPage(
  pdfDoc: PDFDocument,
  order: OrderSummary,
  items: LineItemForPrint[],
  pageNumber: number,
  totalPages: number,
  koreanBoldFontBytes: ArrayBuffer,
): Promise<void> {
  const page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const koreanBoldFont = await pdfDoc.embedFont(koreanBoldFontBytes);
  const totalQty = order.lineItems.reduce((sum, item) => sum + item.quantity, 0);

  // Title
  page.drawText('PACKING', {
    x: MARGIN,
    y: PAGE_HEIGHT - 18,
    size: 14,
    font: boldFont,
    color: rgb(0, 0, 0),
  });

  // Order ID
  page.drawText(`#${order.orderId}`, {
    x: MARGIN,
    y: PAGE_HEIGHT - 32,
    size: 10,
    font: boldFont,
    color: rgb(0, 0, 0),
  });

  // Total Qty
  page.drawText(`총수량: ${totalQty}`, {
    x: PAGE_WIDTH - MARGIN - 55,
    y: PAGE_HEIGHT - 25,
    size: 12,
    font: koreanBoldFont,
    color: rgb(0, 0, 0),
  });

  page.drawLine({
    start: { x: MARGIN, y: PAGE_HEIGHT - 40 },
    end: { x: PAGE_WIDTH - MARGIN, y: PAGE_HEIGHT - 40 },
    thickness: 1,
    color: rgb(0, 0, 0),
  });

  const tableWidth = PAGE_WIDTH - 2 * MARGIN;
  const noColWidth = 25;
  const qtyColWidth = 30;
  const itemColWidth = tableWidth - noColWidth - qtyColWidth;

  const headerY = PAGE_HEIGHT - 48;

  page.drawRectangle({
    x: MARGIN,
    y: headerY - HEADER_HEIGHT,
    width: tableWidth,
    height: HEADER_HEIGHT,
    borderColor: rgb(0, 0, 0),
    borderWidth: 1,
    color: rgb(0.85, 0.85, 0.85),
  });

  page.drawText('No', {
    x: MARGIN + 5,
    y: headerY - 16,
    size: 9,
    font: boldFont,
    color: rgb(0, 0, 0),
  });

  page.drawLine({
    start: { x: MARGIN + noColWidth, y: headerY },
    end: { x: MARGIN + noColWidth, y: headerY - HEADER_HEIGHT },
    thickness: 1,
    color: rgb(0, 0, 0),
  });

  page.drawText('상품명', {
    x: MARGIN + noColWidth + 5,
    y: headerY - 16,
    size: 10,
    font: koreanBoldFont,
    color: rgb(0, 0, 0),
  });

  page.drawLine({
    start: { x: MARGIN + noColWidth + itemColWidth, y: headerY },
    end: { x: MARGIN + noColWidth + itemColWidth, y: headerY - HEADER_HEIGHT },
    thickness: 1,
    color: rgb(0, 0, 0),
  });

  page.drawText('수량', {
    x: MARGIN + noColWidth + itemColWidth + 5,
    y: headerY - 16,
    size: 10,
    font: koreanBoldFont,
    color: rgb(0, 0, 0),
  });

  let currentY = headerY - HEADER_HEIGHT;

  items.forEach((item, index) => {
    const rowY = currentY - ROW_HEIGHT;
    const globalIndex = (pageNumber - 1) * ITEMS_PER_PAGE + index + 1;

    page.drawRectangle({
      x: MARGIN,
      y: rowY,
      width: tableWidth,
      height: ROW_HEIGHT,
      borderColor: rgb(0, 0, 0),
      borderWidth: 1,
    });

    page.drawLine({
      start: { x: MARGIN + noColWidth, y: currentY },
      end: { x: MARGIN + noColWidth, y: rowY },
      thickness: 1,
      color: rgb(0, 0, 0),
    });

    page.drawLine({
      start: { x: MARGIN + noColWidth + itemColWidth, y: currentY },
      end: { x: MARGIN + noColWidth + itemColWidth, y: rowY },
      thickness: 1,
      color: rgb(0, 0, 0),
    });

    page.drawText(globalIndex.toString(), {
      x: MARGIN + 8,
      y: rowY + 10,
      size: 9,
      font: boldFont,
      color: rgb(0, 0, 0),
    });

    const productName = item.nameKor || item.name || '';
    const maxChars = 22;
    const displayName = productName.length > maxChars ? productName.substring(0, maxChars) + '...' : productName;

    page.drawText(displayName, {
      x: MARGIN + noColWidth + 3,
      y: rowY + 10,
      size: 9,
      font: koreanBoldFont,
      color: rgb(0, 0, 0),
    });

    page.drawText(item.quantity.toString(), {
      x: MARGIN + noColWidth + itemColWidth + 8,
      y: rowY + 10,
      size: 11,
      font: boldFont,
      color: rgb(0, 0, 0),
    });

    currentY = rowY;
  });

  const totalRowY = currentY - ROW_HEIGHT;
  page.drawRectangle({
    x: MARGIN,
    y: totalRowY,
    width: tableWidth,
    height: ROW_HEIGHT,
    borderColor: rgb(0, 0, 0),
    borderWidth: 2,
    color: rgb(0.85, 0.85, 0.85),
  });

  page.drawText('합계', {
    x: MARGIN + noColWidth + itemColWidth / 2 - 10,
    y: totalRowY + 10,
    size: 11,
    font: koreanBoldFont,
    color: rgb(0, 0, 0),
  });

  page.drawLine({
    start: { x: MARGIN + noColWidth + itemColWidth, y: currentY },
    end: { x: MARGIN + noColWidth + itemColWidth, y: totalRowY },
    thickness: 1,
    color: rgb(0, 0, 0),
  });

  page.drawText(totalQty.toString(), {
    x: MARGIN + noColWidth + itemColWidth + 8,
    y: totalRowY + 10,
    size: 12,
    font: boldFont,
    color: rgb(0, 0, 0),
  });

  page.drawText(`${pageNumber}/${totalPages}`, {
    x: PAGE_WIDTH - MARGIN - 25,
    y: 12,
    size: 8,
    font,
    color: rgb(0, 0, 0),
  });

  page.drawText('[검수 후 폐기]', {
    x: MARGIN,
    y: 12,
    size: 8,
    font: koreanBoldFont,
    color: rgb(0, 0, 0),
  });
}

export async function appendPackingVerificationPages(
  existingPdfBytes: Uint8Array,
  order: OrderSummary,
): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  pdfDoc.registerFontkit(fontkit);
  const koreanBoldFontBytes = await loadKoreanBoldFont();
  const items = order.lineItems.map((item) => ({
    name: item.name,
    nameKor: item.nameKor,
    quantity: item.quantity,
  }));

  const totalPages = Math.ceil(items.length / ITEMS_PER_PAGE);
  const pagesToCreate = Math.max(1, totalPages);

  for (let i = 0; i < pagesToCreate; i++) {
    const startIdx = i * ITEMS_PER_PAGE;
    const pageItems = items.slice(startIdx, startIdx + ITEMS_PER_PAGE);
    await drawPackingVerificationPage(pdfDoc, order, pageItems, i + 1, pagesToCreate, koreanBoldFontBytes);
  }

  return pdfDoc.save();
}

